import { useForm } from "react-hook-form";
import {
  HealthReportdata,
  HealthReportSchema,
} from "../schema/health-report.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { Button } from "src/components/buttons/shadcn-button";
import { Stethoscope } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "src/components/form/form";
import moment from "moment";
import { Input } from "src/components/input/input";
import { useAddHealthReport } from "../hooks/useAddHealthReport";
import { activity, healthParameters } from "src/utils/healthparams";

const HealthReportForm = ({
  customerId,
  memberId,
}: {
  customerId: number;
  memberId: number;
}) => {
  const form = useForm<HealthReportdata>({
    resolver: zodResolver(HealthReportSchema),
  });

  const { mutate: addHealthReport, isPending } = useAddHealthReport();

  const onSubmit = async (data: HealthReportdata) => {
    if (!customerId && !memberId) {
      toast.error("Select Member First");
    }

    addHealthReport({ data, customerId, memberId });
  };

  return (
    <div className="max-w-5xl bg-card shadow-lg rounded-2xl p-8 border border-gray-100 mx-auto">
      <h2 className="font-medium text-[#42679e] text-xl mb-6 border-b pb-3 flex items-center justify-center gap-3">
        <Stethoscope size={20} /> <span>Health Parameters</span>
      </h2>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6 w-full"
        >
          <div className="w-full rounded-lg">
            <table className="w-full border-collapse text-sm">
              <tbody>
                {/* Header */}
                <tr className="bg-gradient-to-r from-blue-100 to-blue-50">
                  <th className="border border-gray-300 px-3 py-2 text-left font-medium text-[#42679e]">
                    Health Parameters
                  </th>
                  <th
                    className="border border-gray-300 px-3 py-2 text-right font-medium text-[#42679e]"
                    colSpan={3}
                  >
                    Date:&nbsp;
                    <span className="font-medium text-[#42679e]">
                      {moment(new Date()).format("MMM Do YY")}
                    </span>
                  </th>
                </tr>

                {/* General Parameters */}
                {healthParameters.map((param, i) => (
                  <tr
                    key={i}
                    className={`${
                      i % 2 === 0 ? "bg-gray-50" : "bg-white"
                    } hover:bg-blue-50 transition`}
                  >
                    <td className="border border-gray-300 px-3 py-2 text-[#42679e] font-medium">
                      {param.label}
                    </td>
                    <td
                      className="border border-gray-300 px-3 py-2"
                      colSpan={3}
                    >
                      <FormField
                        control={form.control}
                        name={param.name as keyof HealthReportdata}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                {...field}
                                type={param.type}
                                min={param.type === "number" ? 0 : undefined}
                                step={param.type === "number" ? "any" : undefined}
                                value={field.value ?? ""}
                                className="w-full text-center text-sm rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-400 transition text-[#42679e] font-medium px-2 py-2 h-9 box-border"
                                placeholder="Enter value"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </td>
                  </tr>
                ))}

                {/* ABNORMAL MEDICAL REPORTS SECTION */}
                <tr>
                  <td
                    colSpan={4}
                    className="bg-gradient-to-r from-blue-200 to-blue-100 text-center text-sm font-medium text-[#42679e] py-2"
                  >
                    Other Parameters
                  </td>
                </tr>

                <tr className="bg-white">
                  <td
                    className="border border-gray-300 px-2 py-1 text-center font-medium"
                    colSpan={4}
                  >
                    <div className="h-fit grid grid-cols-3 gap-6 text-xs text-[#42679e]">
                      {/* Medical History */}
                      <div className="">
                        <h4 className="text-center font-medium mb-1">
                          Abnormal Blood Reports (not older than 6 month)
                        </h4>
                        <FormField
                          control={form.control}
                          name="medical_history"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="Enter blood reports"
                                  type="text"
                                  value={field.value ?? ""}
                                  className="border rounded-lg px-2 py-2 h-9 box-border"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Medication */}
                      <div className="">
                        <h4 className="text-center font-medium mb-1">
                          Medication
                        </h4>
                        <FormField
                          control={form.control}
                          name="medication"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="Enter medication details"
                                  type="text"
                                  value={field.value ?? ""}
                                  className="border rounded-lg px-2 py-2 h-9 box-border"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Activity Level */}
                      <div className="">
                        <h4 className="text-center font-medium mb-1">
                          Activity Level
                        </h4>
                        <FormField
                          control={form.control}
                          name="activity_level"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <select
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) =>
                                    field.onChange(e.target.value)
                                  }
                                  className="border rounded-lg px-2 py-2 w-full h-9 box-border"
                                >
                                  <option value="">
                                    Select Activity level
                                  </option>
                                  {activity.map((activity, i) => (
                                    <option value={activity} key={i}>
                                      {activity}
                                    </option>
                                  ))}
                                </select>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </td>
                </tr>

                {/* Submit Button */}
                <tr>
                  <td
                    className="border border-gray-300 px-3 py-3 text-center bg-blue-50"
                    colSpan={4}
                  >
                    <Button
                      type="submit"
                      disabled={isPending || form.formState.isSubmitting}
                    >
                      {isPending ? (
                        <span className="loading loading-dots loading-md"></span>
                      ) : (
                        "Submit Health Parameters"
                      )}
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default HealthReportForm;
